// The room the dock (orb, mic, composer) takes over the bottom of the conversation, on the phone
// page. The orb's canvas hangs over the dock's top (a negative margin), so the conversation is
// padded clear of how far the dock really reaches, and its controls that have scrolled under the
// dock (behind the orb's glow and the blurred edge) take no taps: a tap there lands on nothing,
// never on a Play or "That was me" the user can't see. The orb itself takes taps only on its
// glass: its canvas lets them through, and a round button sits over the glass (index.html).

/**
 * How far up the dock reaches from its bottom: its own height plus however far the orb hangs
 * over its top (`orbTop`: the orb's offsetTop in the dock, negative when it hangs over).
 */
export function dockReach(dockHeight: number, orbTop: number): number {
  return Math.ceil(dockHeight + Math.max(0, -orbTop));
}

/** The class that makes a transcript control take no taps (phone.css). */
export const COVERED = "vk-covered";

/**
 * Marks each button in `list` that reaches below `line` (the dock's top, in viewport px) as
 * covered: under the dock, the orb's glow or the blurred edge, so it takes no taps. One partly
 * under counts as covered: a tap on what shows of it could still land on the hidden part.
 */
export function markCovered(list: HTMLElement, line: number): void {
  for (const b of list.querySelectorAll<HTMLElement>("button")) {
    const covered = b.getBoundingClientRect().bottom > line;
    if (b.classList.contains(COVERED) !== covered) b.classList.toggle(COVERED, covered);
  }
}

/**
 * Keeps `list`'s controls under the dock covered as the conversation scrolls, grows or redraws,
 * and the dock moves. `line()`: the dock's top now. `viewport`: the visual viewport, whose resize
 * and scroll move the dock on iOS with nothing else firing (the keyboard's height changing:
 * QuickType, emoji). Each check runs once on the next frame. A button added meanwhile starts out
 * covered (no layout read needed) until that check, so nothing new can be tapped unseen.
 * Returns the function to call when the dock changes size.
 */
export function guardUnderDock(
  scroller: HTMLElement,
  list: HTMLElement,
  line: () => number,
  viewport: EventTarget | null = typeof window === "undefined" ? null : (window.visualViewport ?? null),
): () => void {
  let queued = false;
  const run = () => {
    queued = false;
    markCovered(list, line());
  };
  const later = () => {
    if (!queued) (queued = true), requestAnimationFrame(run);
  };
  const passive = { passive: true };
  scroller.addEventListener("scroll", later, passive);
  window.addEventListener("resize", later, passive);
  viewport?.addEventListener("resize", later, passive);
  viewport?.addEventListener("scroll", later, passive);
  // Lines added or redrawn bring new buttons: covered at once, checked on the next frame.
  if (typeof MutationObserver === "function") {
    new MutationObserver((records) => {
      for (const r of records) {
        for (const n of r.addedNodes) {
          if (!(n instanceof HTMLElement)) continue;
          if (n instanceof HTMLButtonElement) n.classList.add(COVERED);
          for (const b of n.querySelectorAll("button")) b.classList.add(COVERED);
        }
      }
      later();
    }).observe(list, { childList: true, subtree: true });
  }
  run();
  return later;
}
